import { useEffect, useRef, useState } from 'react';

const features = [
  {
    icon: 'ri-heart-pulse-line',
    title: 'Allergy-Friendly',
    desc: 'Children with allergies and intolerances are optimally cared for. A specially prepared meal plan includes fresh and individually packaged meals — safe, balanced and delicious.',
  },
  {
    icon: 'ri-calendar-event-line',
    title: 'Seasonal Variety',
    desc: 'Creative seasonal themed menus and exciting activities turn eating into a real experience. Our monthly action calendar brings highlights that make children eat more consciously.',
  },
  {
    icon: 'ri-plant-line',
    title: 'Up to 70% Organic',
    desc: 'We offer a child-friendly catering concept with up to 70% organic ingredients from ecological farming. Sustainable and healthy — because children deserve the best.',
  },
  {
    icon: 'ri-list-check-3',
    title: '3-Line Meal Plan',
    desc: "Three daily meals to choose from — the DGE menu, the 100% organic menu and the KLASSIKER menu with popular kids' dishes. At least two options can always be individually selected.",
  },
];

const services = [
  {
    key: 'kita',
    tabLabel: 'Kita & Kindergarten',
    tabIcon: 'ri-home-heart-line',
    ageLabel: 'Ages 1–6',
    title: 'Kita & Kindergarten',
    subtitle: 'Tiny tummies, big smiles!',
    description:
      'Little ones grow fast — and they need food that tastes great AND gives them all the energy to play, learn and discover. We cook every meal with love, just the right size for little hands.',
    chips: [
      { label: 'Tiny portions', icon: 'ri-scales-line', color: 'bg-pink-100 text-pink-600 border-pink-200' },
      { label: 'No nasty additives', icon: 'ri-close-circle-line', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
      { label: 'Allergen-safe', icon: 'ri-shield-check-line', color: 'bg-lime-100 text-lime-700 border-lime-200' },
      { label: 'Finger food options', icon: 'ri-hand-heart-line', color: 'bg-orange-100 text-orange-600 border-orange-200' },
      { label: 'Organic ingredients', icon: 'ri-leaf-line', color: 'bg-green-100 text-green-700 border-green-200' },
      { label: 'Daily fresh', icon: 'ri-sun-line', color: 'bg-primary-100 text-primary-600 border-primary-200' },
    ],
    image: 'https://readdy.ai/api/search-image?query=adorable%20happy%20toddler%20children%20sitting%20at%20colorful%20table%20eating%20healthy%20food%20in%20bright%20cheerful%20kindergarten%2C%20warm%20sunlight%2C%20laughing%20kids%20with%20bibs%2C%20colorful%20plates%20with%20vegetables%20and%20pasta%2C%20cozy%20and%20playful%20room%20setting%2C%20professional%20photography%20with%20vibrant%20warm%20colors&width=700&height=500&seq=kita-kids-001&orientation=landscape',
    bg: 'from-pink-50 via-rose-50 to-primary-50',
    accent: 'bg-primary-500',
    accentLight: 'bg-primary-100',
    accentText: 'text-primary-600',
    deco: [
      { icon: 'ri-heart-fill', color: 'text-primary-300', size: 'text-3xl', pos: 'top-6 right-8', rotate: 'rotate-12' },
      { icon: 'ri-star-fill', color: 'text-yellow-300', size: 'text-2xl', pos: 'top-16 right-20', rotate: '-rotate-6' },
      { icon: 'ri-leaf-fill', color: 'text-lime-300', size: 'text-4xl', pos: 'bottom-10 right-6', rotate: 'rotate-45' },
      { icon: 'ri-seedling-fill', color: 'text-green-300', size: 'text-2xl', pos: 'bottom-20 right-24', rotate: '-rotate-12' },
    ],
    tabBg: 'bg-primary-500',
    tabBgInactive: 'bg-primary-50 text-primary-500 hover:bg-primary-100',
  },
  {
    key: 'school',
    tabLabel: 'Schools',
    tabIcon: 'ri-book-open-line',
    ageLabel: 'Ages 6–18',
    title: 'Schools',
    subtitle: 'Brain food for future champions!',
    description:
      'Growing students need real fuel — not junk. Our school menus are crafted to keep brains sharp and bodies strong throughout the whole day, with flavors kids actually look forward to.',
    chips: [
      { label: 'All age groups', icon: 'ri-user-heart-line', color: 'bg-lime-100 text-lime-700 border-lime-200' },
      { label: 'Veggie every day', icon: 'ri-plant-line', color: 'bg-green-100 text-green-700 border-green-200' },
      { label: 'Brain-boosting meals', icon: 'ri-flashlight-line', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
      { label: 'Quick lunch service', icon: 'ri-timer-line', color: 'bg-orange-100 text-orange-600 border-orange-200' },
      { label: '70% organic', icon: 'ri-leaf-line', color: 'bg-teal-100 text-teal-700 border-teal-200' },
      { label: 'Allergen-labeled', icon: 'ri-information-line', color: 'bg-primary-100 text-primary-600 border-primary-200' },
    ],
    image: 'https://readdy.ai/api/search-image?query=diverse%20happy%20school%20children%20enjoying%20healthy%20colorful%20lunch%20in%20bright%20modern%20school%20cafeteria%2C%20smiling%20kids%20with%20food%20trays%2C%20vibrant%20vegetables%20and%20fresh%20meals%2C%20energetic%20atmosphere%2C%20natural%20daylight%20streaming%20in%2C%20warm%20cheerful%20school%20dining%20setting%2C%20professional%20photography&width=700&height=500&seq=school-kids-001&orientation=landscape',
    bg: 'from-lime-50 via-green-50 to-emerald-50',
    accent: 'bg-lime-500',
    accentLight: 'bg-lime-100',
    accentText: 'text-lime-700',
    deco: [
      { icon: 'ri-star-fill', color: 'text-yellow-300', size: 'text-3xl', pos: 'top-6 right-8', rotate: 'rotate-12' },
      { icon: 'ri-heart-fill', color: 'text-lime-300', size: 'text-2xl', pos: 'top-16 right-20', rotate: '-rotate-6' },
      { icon: 'ri-flashlight-fill', color: 'text-orange-200', size: 'text-4xl', pos: 'bottom-10 right-6', rotate: 'rotate-15' },
      { icon: 'ri-leaf-fill', color: 'text-green-300', size: 'text-2xl', pos: 'bottom-20 right-24', rotate: '-rotate-12' },
    ],
    tabBg: 'bg-lime-500',
    tabBgInactive: 'bg-lime-50 text-lime-600 hover:bg-lime-100',
  },
];

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function ServicesOverview() {
  const serviceAnim = useInView(0.1);
  const testimonialAnim = useInView(0.1);
  const featuresAnim = useInView(0.1);
  const [activeTab, setActiveTab] = useState(0);
  const [sliding, setSliding] = useState(false);

  // Listen for tab switch events dispatched from Hero gateway cards
  useEffect(() => {
    const handler = (e: Event) => {
      const idx = (e as CustomEvent<{ tab: number }>).detail.tab;
      if (idx === activeTab) return;
      setSliding(true);
      setTimeout(() => {
        setActiveTab(idx);
        setSliding(false);
      }, 260);
    };
    window.addEventListener('serviceTabSwitch', handler);
    return () => window.removeEventListener('serviceTabSwitch', handler);
  }, [activeTab]);

  const switchTab = (idx: number) => {
    if (idx === activeTab) return;
    setSliding(true);
    setTimeout(() => {
      setActiveTab(idx);
      setSliding(false);
    }, 260);
  };

  const svc = services[activeTab];

  return (
    <section className="bg-white overflow-hidden">

      {/* ── Colleague Testimonial ── */}
      <div ref={testimonialAnim.ref} className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          <div className={`w-full lg:w-96 flex-shrink-0 transition-all duration-700 ${testimonialAnim.inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20female%20project%20manager%20at%20catering%20company%20smiling%20confidently%2C%20business%20casual%20attire%2C%20modern%20office%20or%20kitchen%20background%2C%20warm%20natural%20lighting%2C%20portrait%20photography%2C%20authentic%20and%20approachable%20look&width=600&height=460&seq=colleague-001&orientation=portrait"
                alt="Colleague testimonial"
                className="w-full h-72 lg:h-80 object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <div className="absolute bottom-4 left-4">
                <p className="text-white font-semibold text-base leading-tight">Caro Winges</p>
                <p className="text-white/75 text-sm">Project Manager, Lunch4You</p>
              </div>
            </div>
          </div>
          <div className={`flex-1 transition-all duration-700 delay-200 ${testimonialAnim.inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 border border-primary-200 rounded-full mb-4">
              <i className="ri-team-line text-primary-500 text-sm"></i>
              <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">From Colleague to Colleague</span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2 leading-snug">Added Value on All Sides</h2>
            <div className="w-12 h-1 bg-primary-500 rounded-full mb-5"></div>
            <blockquote className="text-gray-700 text-base lg:text-lg leading-relaxed mb-6 pl-5 border-l-4 border-primary-500 italic">
              "As an employee in catering — and also as a mother — I know how important good food for children is. In schools and kindergartens, catering should not be left to unprofessional providers or pure contract business. That is why I think it is right that we pass on our knowledge from many years of experience and contribute to children everywhere getting a healthy lunch at fair prices."
            </blockquote>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 bg-white rounded-xl px-4 py-3 border border-gray-100">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-map-pin-line text-primary-500"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500 leading-none">Currently serving</p>
                  <p className="text-sm font-bold text-gray-900">3.5 Mio. Meals / Year</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-xl px-4 py-3 border border-gray-100">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-lime-100">
                  <i className="ri-leaf-line text-lime-600"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500 leading-none">System</p>
                  <p className="text-sm font-bold text-gray-900">BIOLOGISCH certified</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-xl px-4 py-3 border border-gray-100">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-yellow-100">
                  <i className="ri-star-fill text-yellow-500"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500 leading-none">Established</p>
                  <p className="text-sm font-bold text-gray-900">Region-wide market leader</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          ── Kid-Friendly Kita & Schools Section ──
          ══════════════════════════════════════════ */}
      <div id="services-section" ref={serviceAnim.ref} className="py-16 px-4 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto">

          {/* Section heading */}
          <div className={`text-center mb-10 transition-all duration-700 ${serviceAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-dashed border-primary-300 bg-primary-50 mb-4">
              <i className="ri-star-smile-line text-primary-500 text-base"></i>
              <span className="text-primary-600 text-xs font-bold uppercase tracking-wider">Who We Cook For</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 leading-tight">
              Children Are Our{' '}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-primary-500 to-pink-400 bg-clip-text text-transparent">Future</span>
                <span className="absolute -bottom-1 left-0 w-full h-1 bg-gradient-to-r from-primary-400 to-pink-300 rounded-full"></span>
              </span>
            </h2>
            <p className="text-gray-500 mt-3 max-w-lg mx-auto text-sm">
              From tiny toddlers to teenagers — we tailor every meal to the age, taste and energy needs of every child.
            </p>
          </div>

          {/* ── Tab Toggle ── */}
          <div className={`flex justify-center mb-8 transition-all duration-700 delay-100 ${serviceAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="inline-flex bg-gray-100 rounded-2xl p-1.5 gap-1">
              {services.map((s, idx) => (
                <button
                  key={s.key}
                  onClick={() => switchTab(idx)}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeTab === idx
                      ? `${s.tabBg} text-white scale-105`
                      : `text-gray-500 hover:text-gray-700 hover:bg-white`
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${s.tabIcon} text-base`}></i>
                  </div>
                  {s.tabLabel}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${activeTab === idx ? 'bg-white/25 text-white' : 'bg-gray-200 text-gray-500'}`}>
                    {s.ageLabel}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* ── Active Service Panel ── */}
          <div
            className={`rounded-3xl overflow-hidden bg-gradient-to-br ${svc.bg} border-2 border-dashed ${activeTab === 0 ? 'border-primary-200' : 'border-lime-200'} transition-all duration-300 ${sliding ? 'opacity-0 scale-[0.98]' : 'opacity-100 scale-100'}`}
          >
            <div className="flex flex-col lg:flex-row min-h-[420px]">

              {/* Left: Image with fun sticker overlays */}
              <div className="relative w-full lg:w-[45%] flex-shrink-0 overflow-hidden min-h-[280px] lg:min-h-0">
                <img
                  src={svc.image}
                  alt={svc.title}
                  className="w-full h-full object-cover object-top"
                />
                {/* Dark overlay at bottom */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/10"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>

                {/* Age badge sticker */}
                <div className={`absolute top-5 left-5 ${svc.accent} text-white px-4 py-2 rounded-2xl font-extrabold text-sm rotate-[-3deg]`}>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${svc.tabIcon} text-sm`}></i>
                    </div>
                    {svc.ageLabel}
                  </div>
                </div>

                {/* Floating food decoration icons */}
                {svc.deco.map((d, di) => (
                  <div
                    key={di}
                    className={`absolute ${d.pos} ${d.color} ${d.size} ${d.rotate} opacity-60 pointer-events-none`}
                    style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
                  >
                    <i className={d.icon}></i>
                  </div>
                ))}

                {/* Bottom left label */}
                <div className="absolute bottom-5 left-5 bg-white/90 backdrop-blur-sm rounded-2xl px-4 py-2.5">
                  <p className="text-gray-400 text-xs leading-none mb-0.5">Serving</p>
                  <p className="text-gray-900 font-extrabold text-sm">{svc.title}</p>
                </div>
              </div>

              {/* Right: Content */}
              <div className="flex-1 p-8 lg:p-10 flex flex-col justify-center">

                {/* Fun subtitle */}
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 ${svc.accentLight} rounded-full mb-4 self-start`}>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-smile-line text-yellow-500 text-sm"></i>
                  </div>
                  <span className={`text-xs font-bold ${svc.accentText}`}>{svc.subtitle}</span>
                </div>

                {/* Title */}
                <h3 className="text-2xl lg:text-3xl font-extrabold text-gray-900 mb-3 leading-snug">
                  {svc.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 text-sm lg:text-base leading-relaxed mb-6">
                  {svc.description}
                </p>

                {/* Colorful feature chips */}
                <div className="flex flex-wrap gap-2 mb-7">
                  {svc.chips.map((chip) => (
                    <span
                      key={chip.label}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${chip.color}`}
                    >
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className={`${chip.icon} text-xs`}></i>
                      </div>
                      {chip.label}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                <div className="flex items-center gap-4 flex-wrap">
                  <button
                    className={`flex items-center gap-2 px-6 py-3 ${svc.accent} text-white font-bold text-sm rounded-2xl hover:opacity-90 hover:scale-105 active:scale-95 transition-all cursor-pointer whitespace-nowrap`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-information-line text-sm"></i>
                    </div>
                    Learn More
                  </button>
                  <button className={`flex items-center gap-1.5 text-sm font-semibold ${svc.accentText} hover:underline cursor-pointer whitespace-nowrap`}>
                    See sample menu
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* ── Fun Stats Footer Bar ── */}
            <div className={`border-t-2 border-dashed ${activeTab === 0 ? 'border-primary-200' : 'border-lime-200'} px-8 py-5 flex flex-wrap gap-6 justify-around`}>
              {[
                { icon: 'ri-bowl-line', value: 'Fresh daily', label: 'Every single day' },
                { icon: 'ri-leaf-line', value: '70% organic', label: 'Regional produce' },
                { icon: 'ri-shield-check-line', value: 'Allergen-safe', label: 'Labeled & verified' },
                { icon: 'ri-star-smile-line', value: 'Kids approved', label: 'Flavors they love' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${svc.accentLight}`}>
                    <i className={`${item.icon} ${svc.accentText} text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-sm leading-none">{item.value}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{item.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* ── Food Offering Features ── */}
      <div ref={featuresAnim.ref} className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className={`text-center mb-12 transition-all duration-700 ${featuresAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 border border-primary-200 rounded-full mb-4">
              <i className="ri-bowl-line text-primary-500 text-sm"></i>
              <span className="text-primary-600 text-xs font-semibold uppercase tracking-wider">The Offering</span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">
              Really Delicious &amp; Balanced Food
            </h2>
            <p className="text-gray-500 mt-2 max-w-xl mx-auto text-sm lg:text-base">
              Every meal is thoughtfully planned to be nutritious, safe, and genuinely enjoyable for every child.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <div
                key={f.title}
                className={`bg-white rounded-2xl p-6 border border-gray-100 hover:border-primary-200 hover:shadow-md transition-all duration-700 group ${featuresAnim.inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 group-hover:bg-primary-100 transition-colors mb-4">
                  <i className={`${f.icon} text-primary-500 text-xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base mb-2">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
